import React, { useState, useEffect } from 'react';
import useAddRecipe from '../hooks/addRecipe';
import styles from './AddRecipeModal.module.css';
import { useAuth } from '../context/AuthContext';
import { fetchWithCSRF } from '../utils/csrfUtils';
import { Button } from 'react-bootstrap';
import { useAddRecipeImage } from '../hooks/getRecipeImage';

const AddRecipeModal = ({ isOpen, onClose, onSuccess, onError }) => {
    const [currentSlide, setCurrentSlide] = useState(1);
    const [showNameError, setShowNameError] = useState(false);
    const [recipeData, setRecipeData] = useState({
        name: '',
        description: '',
        image: null,
        numberOfServings: 1,
        prepTime: '',
        cookTime: '',
        difficulty: 'Easy',
        nutritionalInfo: {
            calories: '',
            protein: '',
            carbohydrate: '',
            fat: ''
        },
        ingredients: [],
        steps: [],
        cuisines: [],
        diets: [],
        recipeTimes: []
    });
    const [allCuisines, setAllCuisines] = useState([]);
    const [allDiets, setAllDiets] = useState([]);
    const [allRecipeTimes, setAllRecipeTimes] = useState([]);
    const [newStepInstruction, setNewStepInstruction] = useState('');
    const [allIngredients, setAllIngredients] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [tempSelectedIngredient, setTempSelectedIngredient] = useState(null);
    const [newIngredient, setNewIngredient] = useState({ 
        ingredient_id: '', 
        ingredient_name: '', 
        quantity: '' 
    });
    const [recipeAlreadyCreated, setRecipeAlreadyCreated] = useState(false);
    const { addRecipe, isLoading } = useAddRecipe();
    const { sessionToken } = useAuth();
    const { uploadImage, isUploading, error: uploadError } = useAddRecipeImage();
    const [showAddRecipeError, setShowAddRecipeError] = useState(false);
    const [addRecipeError, setAddRecipeError] = useState(null);

    // Fetch all cuisines, diets, and ingredients when modal opens
    useEffect(() => {
        if (isOpen && sessionToken) {
            // Fetch cuisines
            fetchWithCSRF('/api/v1/cuisine', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${sessionToken}`
                }
            })
                .then(response => response.json())
                .then(data => setAllCuisines(data))
                .catch(error => console.error('Error fetching cuisines:', error));

            // Fetch diets
            fetchWithCSRF('/api/v1/diet', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${sessionToken}`
                }
            })
                .then(response => response.json())
                .then(data => setAllDiets(data))
                .catch(error => console.error('Error fetching diets:', error));

            // Fetch ingredients
            fetchWithCSRF('/api/v1/ingredient', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${sessionToken}`
                }
            })
                .then(response => response.json())
                .then(data => setAllIngredients(data))
                .catch(error => console.error('Error fetching ingredients:', error));

            // Fetch recipe times enum
            fetchWithCSRF('/api/v1/recipe-time/enum', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${sessionToken}`
                }
            })
                .then(response => response.json())
                .then(data => setAllRecipeTimes(data))
                .catch(error => console.error('Error fetching recipe times:', error));
        }
    }, [isOpen]);

    const handleAddIngredient = () => {
        if (newIngredient.ingredient_id && newIngredient.quantity) {
            setRecipeData(prev => ({
                ...prev,
                ingredients: [...prev.ingredients, newIngredient]
            }));
            setNewIngredient({ ingredient_id: '', ingredient_name: '', quantity: '' });
            setTempSelectedIngredient(null);
            setSearchTerm('');
        }
    };

    const handleRemoveIngredient = (ingredientId) => {
        setRecipeData(prev => ({
            ...prev,
            ingredients: prev.ingredients.filter(ing => ing.ingredient_id !== ingredientId)
        }));
    };

    const handleAddStep = () => {
        if (!newStepInstruction.trim()) return;

        setRecipeData(prev => ({
            ...prev,
            steps: [...prev.steps, {
                stepNumber: prev.steps.length + 1,
                instruction: newStepInstruction
            }]
        }));
        setNewStepInstruction('');
    };

    const handleRemoveStep = (stepNumberToRemove) => {
        setRecipeData(prev => {
            // Remove the step and reorder remaining steps
            const filteredSteps = prev.steps
                .filter(step => step.stepNumber !== stepNumberToRemove)
                .map((step, index) => ({
                    ...step,
                    stepNumber: index + 1
                }));

            return {
                ...prev,
                steps: filteredSteps
            };
        });
    };

    const handleCuisineToggle = (cuisineId) => {
        setRecipeData(prev => {
            const cuisines = prev.cuisines.includes(cuisineId)
                ? prev.cuisines.filter(id => id !== cuisineId)
                : [...prev.cuisines, cuisineId];
            return {
                ...prev,
                cuisines
            };
        });
    };

    const handleDietToggle = (dietId) => {
        setRecipeData(prev => {
            const diets = prev.diets.includes(dietId)
                ? prev.diets.filter(id => id !== dietId)
                : [...prev.diets, dietId];
            return {
                ...prev,
                diets
            };
        });
    };

    const handleRecipeTimeToggle = (time) => {
        setRecipeData(prev => {
            const recipeTimes = prev.recipeTimes.includes(time)
                ? prev.recipeTimes.filter(t => t !== time)
                : [...prev.recipeTimes, time];
            return {
                ...prev,
                recipeTimes
            };
        });
    };

    const filteredIngredients = allIngredients.filter(ingredient =>
        ingredient.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'name' && value.trim()) {
            setShowNameError(false);
        }
        if (name.includes('.')) {
            // Handle nested nutritionalInfo object
            const [parent, child] = name.split('.');
            setRecipeData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setRecipeData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        setRecipeData(prev => ({
            ...prev,
            image: file
        }));
    };

    const [createdRecipeId, setCreatedRecipeId] = useState(null);

    const handleNext = async () => {
        if (currentSlide === 1 && !recipeData.name.trim()) {
            setShowNameError(true);
            return;
        }

        // If we're on nutritional info (step 3) and moving to step 4, create the recipe first
        if (currentSlide === 3 && !recipeAlreadyCreated) {
            const formattedData = {
                name: recipeData.name,
                description: recipeData.description,
                servings: parseInt(recipeData.numberOfServings) || 1,
                prep_time: parseInt(recipeData.prepTime) || 0,
                cook_time: parseInt(recipeData.cookTime) || 0,
                total_time: (parseInt(recipeData.prepTime) || 0) + (parseInt(recipeData.cookTime) || 0),
                difficulty: recipeData.difficulty,
                nutritionalInfo: {
                    calories: parseInt(recipeData.nutritionalInfo.calories) || 0,
                    protein: parseInt(recipeData.nutritionalInfo.protein) || 0,
                    carbohydrate: parseInt(recipeData.nutritionalInfo.carbohydrate) || 0,
                    fat: parseInt(recipeData.nutritionalInfo.fat) || 0
                }
            };

            if (!recipeAlreadyCreated) {
                try {
                    const result = await addRecipe(formattedData);
                    if (result.success) {
                        setCreatedRecipeId(result.recipeId);
                        console.log("Recipe created with ID:", result.recipeId);
                        setRecipeAlreadyCreated(true);

                        // If there's an image selected, upload it
                        if (recipeData.image) {
                            console.log("Uploading image for recipe:", result.recipeId);
                            const uploadSuccess = await uploadImage(result.recipeId, recipeData.image);
                            if (!uploadSuccess) {
                                console.error("Failed to upload recipe image");
                                // We don't block progression here since the recipe was created successfully
                            }
                        }
                    } else {
                        setShowAddRecipeError(true);
                        setAddRecipeError("An error occurred while creating the recipe. Please refresh the page and try again.");
                        return; // Don't proceed to next step if recipe creation failed
                    }
                } catch (error) {
                    console.error("Error creating recipe:", error);
                    setShowAddRecipeError(true);
                    setAddRecipeError("An error occurred while creating the recipe. Please refresh the page and try again.");
                    return;
                }
            }
        }

        setCurrentSlide(prev => prev + 1);
        setShowNameError(false);
    };

    const handleBack = () => {
        setCurrentSlide(prev => prev - 1);
    };

    const handleSubmit = async () => {
        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            // Create all ingredient associations
            const ingredientPromises = recipeData.ingredients.map(ingredient => 
                fetchWithCSRF('/api/v1/recipe-ingredient', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${sessionToken}`
                    },
                    body: JSON.stringify({
                        recipe_id: createdRecipeId,
                        ingredient_id: ingredient.ingredient_id,
                        quantity: ingredient.quantity
                    }),
                })
            );

            // Create all recipe steps
            const stepPromises = recipeData.steps.map(step => 
                fetchWithCSRF('/api/v1/recipe-step', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${sessionToken}`
                    },
                    body: JSON.stringify({
                        recipe_id: createdRecipeId,
                        step_number: step.stepNumber,
                        instruction: step.instruction
                    }),
                })
            );

            // Create all cuisine associations
            const cuisinePromises = recipeData.cuisines.map(cuisineId => 
                fetchWithCSRF('/api/v1/recipe-cuisine', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${sessionToken}`
                    },
                    body: JSON.stringify({
                        recipe_id: createdRecipeId,
                        cuisine_id: cuisineId
                    }),
                })
            );

            // Create all diet associations
            const dietPromises = recipeData.diets.map(dietId => 
                fetchWithCSRF('/api/v1/recipe-diet', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${sessionToken}`
                    },
                    body: JSON.stringify({
                        recipe_id: createdRecipeId,
                        diet_id: dietId
                    }),
                })
            );

            // Create all recipe time associations
            const recipeTimePromises = recipeData.recipeTimes.map(time => 
                fetchWithCSRF('/api/v1/recipe-time', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${sessionToken}`
                    },
                    body: JSON.stringify({
                        recipe_id: createdRecipeId,
                        time: time
                    }),
                })
            );

            // Wait for all promises to complete
            await Promise.all([...ingredientPromises, ...stepPromises, ...cuisinePromises, ...dietPromises, ...recipeTimePromises]);

            // Reset form
            setRecipeData({
                name: '',
                description: '',
                image: null,
                prepTime: '',
                cookTime: '',
                difficulty: 'Easy',
                nutritionalInfo: {
                    calories: '',
                    protein: '',
                    carbohydrate: '',
                    fat: ''
                },
                ingredients: [],
                steps: [],
                cuisines: [],
                diets: [],
                recipeTimes: []
            });
            setCurrentSlide(1);
            setCreatedRecipeId(null);
            onSuccess?.();
        } catch (error) {
            console.error('Error adding ingredients:', error);
            onError?.('Failed to add ingredients to recipe');
        }
    };

    if (!isOpen) return null;

    const renderSlide = () => {
        switch (currentSlide) {
            case 1:
                return (
                    <div className={styles.slide}>
                        {/* <h2>Basic Information</h2> */}
                        <div className={styles['form-group']}>
                            <h2>Create your own recipe</h2>
                            <label htmlFor="name" className="mt-5">Recipe Name <span className={styles.required}>*</span></label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={recipeData.name}
                                onChange={handleInputChange}
                                required
                                className={`form-control-custom ${showNameError ? styles['input-error'] : ''}`}
                            />
                            {showNameError && (
                                <div className={styles['error-text']}>Recipe name is required</div>
                            )}
                        </div>
                        {/* <div className={styles['form-group']}>
                            <label htmlFor="description">Description</label>
                            <textarea
                                id="description"
                                name="description"
                                value={recipeData.description}
                                onChange={handleInputChange}
                                required
                                className="form-control-custom"
                            />
                        </div> */}
                        <div className={styles['form-group']}>
                            <label htmlFor="image">Recipe Image</label>
                            <p className="text-muted" style={{fontSize: '14px'}}>(5 MB max. file size)</p>
                            <input
                                type="file"
                                id="image"
                                accept="image/*"
                                onChange={handleImageUpload}
                            />
                        </div>
                    </div>
                );
            case 2:
                return (
                    <div className={styles.slide}>
                        {/* <h2>Time and Difficulty</h2> */}
                        <div className={styles['form-group']}>
                            <label className="mt-5" htmlFor="numberOfServings">How many servings does this recipe make?</label>
                            <div className={styles['plus-minus-buttons']}>
                                <button
                                    type="button"
                                    onClick={() => handleInputChange({
                                        target: {
                                            name: 'numberOfServings',
                                            value: Math.max(1, parseInt(recipeData.numberOfServings || 1) - 1)
                                        }
                                    })}
                                    className={styles['difficulty-button']}
                                >
                                    -
                                </button>
                                <span>{recipeData.numberOfServings || 1}</span>
                                <button
                                    type="button"
                                    onClick={() => handleInputChange({
                                        target: {
                                            name: 'numberOfServings',
                                            value: Math.min(16, parseInt(recipeData.numberOfServings || 1) + 1)
                                        }
                                    })}
                                    className={styles['difficulty-button']}
                                    disabled={parseInt(recipeData.numberOfServings || 1) >= 16}
                                >
                                    +
                                </button>
                            </div>

                            <label className="mt-4" htmlFor="prepTime">Prep Time (minutes)</label>
                            <div className={styles['plus-minus-buttons']}>
                                <button
                                    type="button"
                                    onClick={() => handleInputChange({
                                        target: {
                                            name: 'prepTime',
                                            value: Math.max(0, parseInt(recipeData.prepTime || 0) - 5)
                                        }
                                    })}
                                    className={styles['difficulty-button']}
                                >
                                    -
                                </button>
                                <span>{recipeData.prepTime || 0}</span>
                                <button
                                    type="button"
                                    onClick={() => handleInputChange({
                                        target: {
                                            name: 'prepTime',
                                            value: parseInt(recipeData.prepTime || 0) + 5
                                        }
                                    })}
                                    className={styles['difficulty-button']}
                                >
                                    +
                                </button>
                            </div>
                        </div>
                        <div className={styles['form-group']}>
                            <label className="mt-4" htmlFor="cookTime">Cook Time (minutes)</label>
                            <div className={styles['plus-minus-buttons']}>
                                <button
                                    type="button"
                                    onClick={() => handleInputChange({
                                        target: {
                                            name: 'cookTime',
                                            value: Math.max(0, parseInt(recipeData.cookTime || 0) - 5)
                                        }
                                    })}
                                    className={styles['difficulty-button']}
                                >
                                    -
                                </button>
                                <span>{recipeData.cookTime || 0}</span>
                                <button
                                    type="button"
                                    onClick={() => handleInputChange({
                                        target: {
                                            name: 'cookTime',
                                            value: parseInt(recipeData.cookTime || 0) + 5
                                        }
                                    })}
                                    className={styles['difficulty-button']}
                                >
                                    +
                                </button>
                            </div>
                        </div>
                        <div className={styles['form-group']}>
                            <label>Difficulty</label>
                            <div className={styles['difficulty-buttons']}>
                                <button
                                    type="button"
                                    className={`${styles['difficulty-button']} ${recipeData.difficulty === 'Easy' ? styles.active : ''}`}
                                    onClick={() => handleInputChange({target: {name: 'difficulty', value: 'Easy'}})}
                                >
                                    Easy
                                </button>
                                <button
                                    type="button"
                                    className={`${styles['difficulty-button']} ${recipeData.difficulty === 'Medium' ? styles.active : ''}`}
                                    onClick={() => handleInputChange({target: {name: 'difficulty', value: 'Medium'}})}
                                >
                                    Medium
                                </button>
                                <button
                                    type="button"
                                    className={`${styles['difficulty-button']} ${recipeData.difficulty === 'Hard' ? styles.active : ''}`}
                                    onClick={() => handleInputChange({target: {name: 'difficulty', value: 'Hard'}})}
                                >
                                    Hard
                                </button>
                            </div>
                        </div>
                    </div>
                );
            case 3:
                return (
                    <div className={styles.slide}>
                        <h2>Nutritional Information</h2>
                        
                        <div className={styles['nutritional-info-group']}>
                            <label className="pt-2" htmlFor="calories">Calories (per serving)</label>
                            <input
                                type="number"
                                id="calories"
                                name="nutritionalInfo.calories"
                                value={recipeData.nutritionalInfo.calories}
                                onChange={handleInputChange}
                                className={`form-control-custom ${styles['nutritional-info-input']}`}
                            />
                        </div>
                        <div className={styles['nutritional-info-group']}>
                            <label className="pt-2" htmlFor="protein">Protein</label>
                            <input
                                type="number"
                                id="protein"
                                name="nutritionalInfo.protein"
                                value={recipeData.nutritionalInfo.protein}
                                onChange={handleInputChange}
                                className={`form-control-custom ${styles['nutritional-info-input']}`}
                            />
                        </div>
                        <div className={styles['nutritional-info-group']}>
                            <label className="pt-2" htmlFor="carbohydrate">Carbohydrates</label>
                            <input
                                type="number"
                                id="carbohydrate"
                                name="nutritionalInfo.carbohydrate"
                                value={recipeData.nutritionalInfo.carbohydrate}
                                onChange={handleInputChange}
                                className={`form-control-custom ${styles['nutritional-info-input']}`}
                            />
                        </div>
                        <div className={styles['nutritional-info-group']}>
                            <label className="pt-2" htmlFor="fat">Fat</label>
                            <input
                                type="number"
                                id="fat"
                                name="nutritionalInfo.fat"
                                value={recipeData.nutritionalInfo.fat}
                                onChange={handleInputChange}
                                className={`form-control-custom ${styles['nutritional-info-input']}`}
                            />
                        </div>
                        <p className="mt-3">Adding nutritional information is optional. If left blank, values will be calculated based on the ingredients.</p>
                        {showAddRecipeError && (
                            <div className="alert alert-danger mt-3">
                                {addRecipeError}
                            </div>
                        )}
                        {uploadError && (
                            <div className="alert alert-warning mt-3">
                                Note: Recipe was created but image upload failed. You can add an image later by editing the recipe.
                            </div>
                        )}
                    </div>
                );
            case 4:
                return (
                    <div className={styles.slide}>
                        <h2>Meal Times</h2>
                        <p className={styles['helper-text']}>Select when this recipe is typically served</p>
                        <div className={styles['cuisine-grid']}>
                            {allRecipeTimes.map((time) => (
                                <button
                                    key={time.value}
                                    type="button"
                                    className={`${styles['cuisine-option']} ${recipeData.recipeTimes.includes(time.value) ? styles.active : ''}`}
                                    onClick={() => handleRecipeTimeToggle(time.value)}
                                >
                                    <div className={styles['cuisine-content']}>
                                        {time.value === 'Breakfast' && <span style={{fontSize: '48px'}}>🍳</span>}
                                        {time.value === 'Lunch' && <span style={{fontSize: '48px'}}>🥪</span>}
                                        {time.value === 'Dinner' && <span style={{fontSize: '48px'}}>🍽️</span>}
                                        {time.value === 'Snack' && <span style={{fontSize: '48px'}}>🥕</span>}
                                        <h4 className={styles['cuisine-name']}>{time.value}</h4>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                );

            case 5:
                return (
                    <div className={styles.slide}>
                        <h2>Recipe Ingredients</h2>
                        <div className={styles['ingredients-list']}>
                            {/* Header row */}
                            <div className={styles['ingredients-header-row']} style={{fontWeight: 'bold', backgroundColor: '#e9ecef'}}>
                                <span className={styles['ingredient-name-header']}>Ingredient Name</span>
                                <span className={styles['ingredient-quantity-header']}>Quantity</span>
                            </div>
                            
                            {/* Existing ingredients */}
                            {recipeData.ingredients.map((ingredient, index) => (
                                <div key={index} className={styles['ingredient-row']}>
                                    <span className={styles['ingredient-name']}>{ingredient.ingredient_name}</span>
                                    <span className={styles['ingredient-quantity']}>{ingredient.quantity}</span>
                                    <span className={styles['ingredient-unit']}>{ingredient.unit_of_measurement}</span>
                                    <button 
                                        onClick={() => handleRemoveIngredient(ingredient.ingredient_id)}
                                        className={styles['remove-button']}
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}
                            
                            {/* New ingredient row */}
                            <div className={styles['new-ingredient-row']}>
                                <div className={styles['ingredient-search-container']}>
                                    <input
                                        type="text"
                                        placeholder="Search ingredients..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className={`form-control-custom ${styles['search-input']}`}
                                    />
                                    {searchTerm && (
                                        <div className={styles['search-results']}>
                                            {filteredIngredients.map(ingredient => (
                                                <div
                                                    key={ingredient.id}
                                                    onClick={() => {
                                                        setNewIngredient({
                                                            ...newIngredient,
                                                            ingredient_id: ingredient.id,
                                                            ingredient_name: ingredient.name,
                                                            unit_of_measurement: ingredient.unit_of_measurement
                                                        });
                                                        setSearchTerm(ingredient.name);
                                                        setTempSelectedIngredient(ingredient.name);
                                                    }}
                                                    className={ingredient.name === tempSelectedIngredient ? 
                                                        styles['search-result-item-selected'] : 
                                                        styles['search-result-item']}
                                                >
                                                    {ingredient.name}
                                                </div>
                                            ))}
                                            {filteredIngredients.length === 0 && (
                                                <div className={styles['no-results']}>No ingredients found</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <input
                                    type="text"
                                    placeholder="Quantity"
                                    value={newIngredient.quantity}
                                    onChange={e => setNewIngredient({ ...newIngredient, quantity: e.target.value })}
                                    className={`form-control-custom ${styles['form-input']}`}
                                />
                                <span>{tempSelectedIngredient ? newIngredient.unit_of_measurement : ''}</span>
                                <button 
                                    type="button" 
                                    onClick={handleAddIngredient}
                                    className={styles['add-ingredient-button']}
                                    disabled={!newIngredient.ingredient_id || !newIngredient.quantity}
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                    </div>
                );

            case 6:
                return (
                    <div className={styles.slide}>
                        <h2>Recipe Steps</h2>
                        <div className={styles['steps-list']}>
                            {recipeData.steps.map((step, index) => (
                                <div key={index} className={styles['step-item']}>
                                    <div className={styles['step-content']}>
                                        <span className={styles['step-number']}>{step.stepNumber}.</span>
                                        <span className={styles['step-instruction']}>{step.instruction}</span>
                                    </div>
                                    <button 
                                        onClick={() => handleRemoveStep(step.stepNumber)}
                                        className={styles['remove-button']}
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div className={styles['step-form']}>
                            <input
                                type="text"
                                placeholder={"Enter step " + (recipeData.steps.length + 1) + " instructions..."}
                                value={newStepInstruction}
                                onChange={e => setNewStepInstruction(e.target.value)}
                                className={`form-control-custom ${styles['step-instruction']}`}
                            />
                            <button 
                                type="button" 
                                onClick={handleAddStep}
                                className={styles['add-button']}
                                disabled={!newStepInstruction.trim()}
                            >
                                Add Step
                            </button>
                        </div>
                    </div>
                );





            case 7:
                return (
                    <div className={styles.slide}>
                        <h2>Recipe Cuisines</h2>
                        <p className={styles['helper-text']}>Select the cuisines that best match your recipe</p>
                        <div className={styles['cuisine-grid']}>
                            {allCuisines.map((cuisine) => (
                                <button
                                    key={cuisine.id}
                                    type="button"
                                    className={`${styles['cuisine-option']} ${recipeData.cuisines.includes(cuisine.id) ? styles.active : ''}`}
                                    onClick={() => handleCuisineToggle(cuisine.id)}
                                >
                                    <div className={styles['cuisine-content']}>
                                        <span className={styles['cuisine-name']}>{cuisine.name}</span>
                                        <div className={styles['cuisine-icon-wrapper']}>
                                            <img 
                                                src={`/icons/cuisineOptions/${cuisine.name.replace(/\s+/g, '')}.svg`}
                                                alt={`${cuisine.name} icon`}
                                                className={styles['cuisine-icon']}
                                                onError={(e) => {
                                                    e.target.onError = null;
                                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxyZWN0IHg9IjMiIHk9IjMiIHdpZHRoPSIxOCIgaGVpZ2h0PSIxOCIgcng9IjIiIHJ5PSIyIj48L3JlY3Q+PGNpcmNsZSBjeD0iOC41IiBjeT0iOC41IiByPSIxLjUiPjwvY2lyY2xlPjxwb2x5bGluZSBwb2ludHM9IjIxIDE1IDEzIDEwIDQgMTciPjwvcG9seWxpbmU+PC9zdmc+';
                                                }}
                                            />
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                );

            case 8:
                return (
                    <div className={styles.slide}>
                        <h2>Recipe Diets</h2>
                        <p className={styles['helper-text']}>Select the diets that this recipe is suitable for</p>
                        <div className={styles['diet-grid']}>
                            {allDiets.map((diet) => (
                                <button
                                    key={diet.id}
                                    type="button"
                                    className={`${styles['diet-option']} ${recipeData.diets.includes(diet.id) ? styles.active : ''}`}
                                    onClick={() => handleDietToggle(diet.id)}
                                >
                                    <div className={styles['diet-content']}>
                                        <span className={styles['diet-name']}>{diet.name}</span>
                                        <div className={styles['diet-icon-wrapper']}>
                                            <img 
                                                src={`/icons/dietOptions/${diet.name.replace(/\s+/g, '')}.jpg`}
                                                alt={`${diet.name} icon`}
                                                className={styles['diet-icon']}
                                                onError={(e) => {
                                                    e.target.onError = null;
                                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxyZWN0IHg9IjMiIHk9IjMiIHdpZHRoPSIxOCIgaGVpZ2h0PSIxOCIgcng9IjIiIHJ5PSIyIj48L3JlY3Q+PGNpcmNsZSBjeD0iOC41IiBjeT0iOC41IiByPSIxLjUiPjwvY2lyY2xlPjxwb2x5bGluZSBwb2ludHM9IjIxIDE1IDEzIDEwIDQgMTciPjwvcG9seWxpbmU+PC9zdmc+';
                                                }}
                                            />
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div 
            className={styles['modal-overlay']} 
            onClick={(e) => {
                if (e.target.className === styles['modal-overlay']) {
                    onClose();
                }
            }}
        >
            <div className={styles['modal-content']}>
                <button className={styles['close-button']} onClick={onClose}>×</button>
                <div className={styles['modal-body']}>
                    {renderSlide()}
                    <div className={styles['modal-footer']}>
                        {currentSlide > 1 && (
                            <button 
                                style={{backgroundColor: 'gray', color: 'white'}}
                                onClick={handleBack}
                            >
                                Back
                            </button>
                        )}
                        {currentSlide < 8 ? (
                            <button 
                                style={{backgroundColor: '#52cd92', color: 'white'}}    
                            onClick={handleNext}
                                disabled={currentSlide === 1 && !recipeData.name.trim()}
                            >
                                Next
                            </button>
                        ) : (
                            <Button 
                                onClick={handleSubmit}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Creating Recipe...' : 'Create Recipe'}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddRecipeModal;
